import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Mail, Trash2, Eye, Archive, Inbox, RefreshCw, CreditCard, CheckCircle2, Clock, DollarSign } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLES = {
  new: "border-brand text-brand",
  read: "border-white/30 text-zinc-300",
  archived: "border-white/10 text-zinc-600",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("inquiries");
  const [payments, setPayments] = useState([]);
  const [paySummary, setPaySummary] = useState({ paid_count: 0, pending_count: 0, paid_total: 0 });
  const [monthly, setMonthly] = useState([]);
  const [billing, setBilling] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inq, st, pay, bill] = await Promise.all([
        api.get("/admin/inquiries"),
        api.get("/admin/stats"),
        api.get("/admin/payments"),
        api.get("/admin/billing-requests"),
      ]);
      setInquiries(inq.data);
      setStats(st.data);
      setPayments(pay.data.items);
      setPaySummary(pay.data.summary);
      setMonthly(pay.data.monthly || []);
      setBilling(bill.data);
    } catch {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success("Inquiry deleted");
      if (selected?.id === id) setSelected(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const filtered = inquiries.filter((i) => (filter === "all" ? true : i.status === filter));

  const StatCard = ({ label, value, icon: Icon }) => (
    <div className="border border-white/10 bg-ink-850 p-5" data-testid={`stat-card-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-jmono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
        <Icon size={16} className="text-brand" />
      </div>
      <div className="font-syne text-4xl font-extrabold tracking-tighter">{value}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ink-950 text-white" data-testid="admin-dashboard-page">
      <header className="border-b border-white/10 sticky top-0 z-20 backdrop-blur-xl bg-black/60">
        <div className="mx-auto max-w-[1400px] px-6 h-16 flex items-center justify-between">
          <div className="font-syne text-lg font-extrabold tracking-tighter">
            GoBuild<span className="text-brand">Web</span>
            <span className="ml-3 font-jmono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Console
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-zinc-400">{user?.email}</span>
            <button
              onClick={load}
              className="p-2 text-zinc-400 hover:text-white transition-colors"
              data-testid="refresh-button"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={doLogout}
              className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-sm hover:border-brand hover:text-brand transition-colors"
              data-testid="logout-button"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 py-8">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button
            onClick={() => setView("inquiries")}
            className={`inline-flex items-center gap-2 font-jmono text-xs uppercase tracking-wider border px-4 py-2 transition-colors ${view === "inquiries" ? "border-brand text-brand" : "border-white/15 text-zinc-400 hover:border-white/40"}`}
            data-testid="tab-inquiries"
          >
            <Inbox size={14} /> Inquiries
          </button>
          <button
            onClick={() => setView("deposits")}
            className={`inline-flex items-center gap-2 font-jmono text-xs uppercase tracking-wider border px-4 py-2 transition-colors ${view === "deposits" ? "border-brand text-brand" : "border-white/15 text-zinc-400 hover:border-white/40"}`}
            data-testid="tab-deposits"
          >
            <CreditCard size={14} /> Deposits
          </button>
          <button
            onClick={() => setView("billing")}
            className={`inline-flex items-center gap-2 font-jmono text-xs uppercase tracking-wider border px-4 py-2 transition-colors ${view === "billing" ? "border-brand text-brand" : "border-white/15 text-zinc-400 hover:border-white/40"}`}
            data-testid="tab-billing"
          >
            <Mail size={14} /> Billing Requests
          </button>
        </div>

        {view === "inquiries" && (
        <>
        <h1 className="font-syne text-3xl font-extrabold tracking-tighter mb-6">Inquiries</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total" value={stats.total} icon={Inbox} />
          <StatCard label="New" value={stats.new} icon={Mail} />
          <StatCard label="Read" value={stats.read} icon={Eye} />
          <StatCard label="Archived" value={stats.archived} icon={Archive} />
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {["all", "new", "read", "archived"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`font-jmono text-xs uppercase tracking-wider border px-4 py-2 transition-colors duration-200 ${
                filter === f ? "border-brand text-brand" : "border-white/15 text-zinc-400 hover:border-white/40"
              }`}
              data-testid={`filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid place-items-center py-24">
            <Loader2 className="animate-spin text-brand" size={28} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-white/10 bg-ink-850 p-16 text-center text-zinc-500" data-testid="empty-state">
            No inquiries here yet.
          </div>
        ) : (
          <div className="border border-white/10 overflow-x-auto" data-testid="inquiries-table">
            <table className="w-full text-left border-collapse min-w-[720px]">
              <thead>
                <tr className="border-b border-white/10 bg-ink-900">
                  {["Name", "Service", "Budget", "Status", "Date", ""].map((h) => (
                    <th key={h} className="font-jmono text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-4 py-3">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr
                    key={i.id}
                    className="border-b border-white/5 hover:bg-ink-900/60 transition-colors cursor-pointer"
                    onClick={() => setSelected(i)}
                    data-testid={`inquiry-row-${i.id}`}
                  >
                    <td className="px-4 py-4">
                      <div className="font-medium">{i.name}</div>
                      <div className="text-xs text-zinc-500">{i.email}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-zinc-400">{i.service || "—"}</td>
                    <td className="px-4 py-4 text-sm text-zinc-400">{i.budget || "—"}</td>
                    <td className="px-4 py-4">
                      <span className={`font-jmono text-[10px] uppercase tracking-wider border px-2 py-1 ${STATUS_STYLES[i.status]}`}>
                        {i.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-zinc-500">
                      {new Date(i.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => updateStatus(i.id, "read")} className="p-1.5 text-zinc-500 hover:text-white transition-colors" title="Mark read" data-testid={`mark-read-${i.id}`}>
                          <Eye size={15} />
                        </button>
                        <button onClick={() => updateStatus(i.id, "archived")} className="p-1.5 text-zinc-500 hover:text-white transition-colors" title="Archive" data-testid={`archive-${i.id}`}>
                          <Archive size={15} />
                        </button>
                        <button onClick={() => remove(i.id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" title="Delete" data-testid={`delete-${i.id}`}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}

        {view === "deposits" && (
        <>
          <h1 className="font-syne text-3xl font-extrabold tracking-tighter mb-6">Deposits</h1>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            <div className="border border-white/10 bg-ink-850 p-5" data-testid="deposit-stat-paid">
              <div className="flex items-center justify-between mb-3">
                <span className="font-jmono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Paid</span>
                <CheckCircle2 size={16} className="text-emerald-400" />
              </div>
              <div className="font-syne text-4xl font-extrabold tracking-tighter">{paySummary.paid_count}</div>
            </div>
            <div className="border border-white/10 bg-ink-850 p-5" data-testid="deposit-stat-pending">
              <div className="flex items-center justify-between mb-3">
                <span className="font-jmono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pending</span>
                <Clock size={16} className="text-brand" />
              </div>
              <div className="font-syne text-4xl font-extrabold tracking-tighter">{paySummary.pending_count}</div>
            </div>
            <div className="border border-white/10 bg-ink-850 p-5" data-testid="deposit-stat-total">
              <div className="flex items-center justify-between mb-3">
                <span className="font-jmono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Collected</span>
                <DollarSign size={16} className="text-emerald-400" />
              </div>
              <div className="font-syne text-4xl font-extrabold tracking-tighter">${paySummary.paid_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>

          <div className="border border-white/10 bg-ink-850 p-5 mb-8" data-testid="revenue-chart">
            <div className="flex items-center justify-between mb-4">
              <span className="font-jmono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Collected — last 6 months</span>
              <DollarSign size={14} className="text-brand" />
            </div>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fill: "#8a8a92", fontSize: 12 }} axisLine={{ stroke: "#2a2a2e" }} tickLine={false} />
                  <YAxis tick={{ fill: "#8a8a92", fontSize: 12 }} axisLine={false} tickLine={false} width={54} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(1) + "k" : v}`} />
                  <Tooltip
                    cursor={{ fill: "rgba(232,178,58,0.08)" }}
                    contentStyle={{ background: "#141416", border: "1px solid #2a2a2e", borderRadius: 8, color: "#fff" }}
                    formatter={(v) => [`$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, "Collected"]}
                  />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]} maxBarSize={54}>
                    {monthly.map((_, idx) => (
                      <Cell key={idx} fill={idx === monthly.length - 1 ? "#E8B23A" : "#5a4a1f"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {loading ? (
            <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-brand" size={28} /></div>
          ) : payments.length === 0 ? (
            <div className="border border-white/10 bg-ink-850 p-16 text-center text-zinc-500" data-testid="deposits-empty">No deposits yet.</div>
          ) : (
            <div className="border border-white/10 overflow-x-auto" data-testid="deposits-table">
              <table className="w-full text-left border-collapse min-w-[720px]">
                <thead>
                  <tr className="border-b border-white/10 bg-ink-900">
                    {["Client", "Package", "Amount", "Status", "Date"].map((h) => (
                      <th key={h} className="font-jmono text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const paid = p.payment_status === "paid";
                    return (
                      <tr key={p.session_id} className="border-b border-white/5 hover:bg-ink-900/60 transition-colors" data-testid={`deposit-row-${p.session_id}`}>
                        <td className="px-4 py-4">
                          <div className="font-medium">{p.customer_name || "—"}</div>
                          <div className="text-xs text-zinc-500">{p.customer_email || "—"}</div>
                        </td>
                        <td className="px-4 py-4 text-sm text-zinc-400">{p.label || p.package_id || "—"}</td>
                        <td className="px-4 py-4 text-sm font-semibold text-white">${p.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1.5 font-jmono text-[10px] uppercase tracking-wider border px-2 py-1 ${paid ? "border-emerald-400/50 text-emerald-400" : "border-brand text-brand"}`}>
                            {paid ? <CheckCircle2 size={11} /> : <Clock size={11} />} {paid ? "Paid" : "Pending"}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-zinc-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
        )}

        {view === "billing" && (
        <>
          <h1 className="font-syne text-3xl font-extrabold tracking-tighter mb-6">Billing Requests</h1>
          {loading ? (
            <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-brand" size={28} /></div>
          ) : billing.length === 0 ? (
            <div className="border border-white/10 bg-ink-850 p-16 text-center text-zinc-500" data-testid="billing-empty">No billing requests yet.</div>
          ) : (
            <div className="border border-white/10 overflow-x-auto" data-testid="billing-table">
              <table className="w-full text-left border-collapse min-w-[720px]">
                <thead>
                  <tr className="border-b border-white/10 bg-ink-900">
                    {["Name", "Business", "Contact", "Request", "Date"].map((h) => (
                      <th key={h} className="font-jmono text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {billing.map((b) => (
                    <tr key={b.id} className="border-b border-white/5 hover:bg-ink-900/60 transition-colors" data-testid={`billing-row-${b.id}`}>
                      <td className="px-4 py-4 font-medium">{b.name || "—"}</td>
                      <td className="px-4 py-4 text-sm text-zinc-400">{b.company || "—"}</td>
                      <td className="px-4 py-4 text-sm text-zinc-400">
                        <a href={`mailto:${b.email}`} className="text-brand hover:underline">{b.email}</a>
                        {b.phone ? <div className="text-xs text-zinc-500">{b.phone}</div> : null}
                      </td>
                      <td className="px-4 py-4 text-sm text-zinc-300 max-w-[320px]"><span className="line-clamp-2">{b.message}</span></td>
                      <td className="px-4 py-4 text-sm text-zinc-500">{b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
        )}
      </main>

      {selected && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
          data-testid="inquiry-detail-overlay"
        >
          <div
            className="h-full w-full max-w-lg bg-ink-900 border-l border-white/10 p-8 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="inquiry-detail-panel"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="font-syne text-2xl font-bold tracking-tight">{selected.name}</h2>
                <a href={`mailto:${selected.email}`} className="text-brand text-sm hover:underline">
                  {selected.email}
                </a>
              </div>
              <button onClick={() => setSelected(null)} className="text-zinc-500 hover:text-white" data-testid="close-detail">
                ✕
              </button>
            </div>
            <dl className="space-y-4 text-sm">
              {[
                ["Company", selected.company || "—"],
                ["Service", selected.service || "—"],
                ["Budget", selected.budget || "—"],
                ["Status", selected.status],
                ["Received", new Date(selected.created_at).toLocaleString()],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-white/10 pb-3">
                  <dt className="font-jmono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{k}</dt>
                  <dd className="text-zinc-300">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6">
              <div className="font-jmono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Message</div>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => updateStatus(selected.id, "read")} className="border border-white/15 px-4 py-2 text-sm hover:border-brand hover:text-brand transition-colors" data-testid="detail-mark-read">
                Mark read
              </button>
              <button onClick={() => updateStatus(selected.id, "archived")} className="border border-white/15 px-4 py-2 text-sm hover:border-brand hover:text-brand transition-colors" data-testid="detail-archive">
                Archive
              </button>
              <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover px-4 py-2 text-sm transition-colors" data-testid="detail-reply">
                <Mail size={14} /> Reply
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
